<template>
  <page-content
    v-if="checkup"
    title="Konsultasi Psikologi: Detail"
    :with-margin="false"
    back-button
  >
    <br />
    <mental-health-checkup-history
      :data="checkup.user.mental_health_checkups"
      class="mx-3"
    />
    <simple-list label="Nama Lengkap" :text="checkup.user.fullname" />
    <simple-list label="Departemen" :text="checkup.user.department?.name" />
    <simple-list label="E-mail" :text="checkup.user.email" />
    <simple-list label="Nomor HP (WhatsApp)">
      <div class="d-flex justify-content-between align-items-center">
        <h4>{{ checkup.user.phone }}</h4>
        <div @click="openSendWhatsappMessageModal()">
          <material-icon
            name="chat"
            type="rounded"
            class="pointer text-success"
            title="Kirim pesan via Whatsapp"
          />
        </div>
      </div>
    </simple-list>
    <simple-list
      label="Hal yang akan dikonsultasikan"
      :text="checkup.problem"
    />
    <simple-list
      label="Jadwal Konsultasi"
      :text="
        getScheduleOptionText(
          checkup.schedule.day,
          checkup.schedule.time_start,
          checkup.schedule.time_end
        )
      "
    />
    <simple-list label="Status">
      <div class="mt-1 d-flex align-items-center flex-wrap">
        <span class="badge mr-1 mb-1" :class="getCheckupBadge(checkup.status)">
          {{ capitalize(getCheckupStatusIndo(checkup.status)) }}
        </span>
        <template v-if="['scheduled', 'completed'].includes(checkup.status)">
          <span
            class="badge flex-shrink-0 d-inline-flex align-items-center mr-1 mb-1 text-gray"
          >
            <material-icon name="event" type="rounded" size="12" class="mr-1" />
            {{ formatDateForHuman(checkup.scheduled_at, true) }}
          </span>
          <span
            class="badge flex-shrink-0 d-inline-flex align-items-center mr-1 mb-1 text-gray"
          >
            <material-icon
              name="schedule"
              type="rounded"
              size="12"
              class="mr-1"
            />
            {{ formatTimeToIndo(checkup.schedule.time_start) }} -
            {{ formatTimeToIndo(checkup.schedule.time_end) }} WIB
          </span>
          <span
            class="badge flex-shrink-0 d-inline-flex align-items-center mb-1 text-gray"
          >
            <material-icon
              name="chair"
              type="outlined"
              size="12"
              class="mr-1"
            />
            {{ checkup.room.name }}
          </span>
        </template>
        <span
          v-if="
            can('update-mental-health-checkup') &&
            checkup.status == 'unscheduled'
          "
          class="badge badge-success flex-shrink-0 d-inline-flex align-items-center mb-1 pointer"
          @click="openAssignScheduleModal()"
        >
          <material-icon
            name="edit_calendar"
            type="rounded"
            size="12"
            class="mr-1"
          />
          Atur Jadwal
        </span>
        <template
          v-if="
            can('update-mental-health-checkup') &&
            ['unscheduled', 'scheduled'].includes(checkup.status)
          "
        >
          <div class="mt-2" style="flex-basis: 100%"></div>
          <span
            v-if="checkup.status == 'scheduled'"
            class="badge badge-success flex-shrink-0 d-inline-flex align-items-center mr-1 mb-1 pointer"
            @click="openAssignScheduleModal()"
          >
            <material-icon
              name="edit_calendar"
              type="rounded"
              size="12"
              class="mr-1"
            />
            Ubah Jadwal
          </span>
          <span
            class="badge badge-danger flex-shrink-0 d-inline-flex align-items-center mr-1 mb-1 pointer"
            @click="cancelSchedule()"
          >
            <material-icon
              name="event_busy"
              type="rounded"
              size="12"
              class="mr-1"
            />
            Batalkan
          </span>
        </template>
      </div>
    </simple-list>
    <simple-list
      v-if="checkup.psychologist"
      label="Psikolog"
      :text="checkup.psychologist.name"
    />
    <!-- Screening -->
    <div class="m-3">
      <h4 class="mb-1">Skrining</h4>
      <div class="card px-0">
        <simple-list
          v-for="(screening, index) in checkup.screenings"
          :key="index"
          :label="screening.question"
          :text="screening.answer"
        />
      </div>
    </div>
    <!-- Suggestion -->
    <div v-if="checkup.status == 'completed'" class="mt-3 mx-3">
      <h4 class="d-flex align-items-center mb-1">
        Solusi/rekomendasi dari psikolog
      </h4>
      <div class="card card-success text-white">
        <div class="unreset">
          <div v-html="checkup.suggestion"></div>
        </div>
        <div class="divider" style="background: rgba(255, 255, 255, 0.4)"></div>
        <div class="d-flex gap-1">
          <span
            v-if="checkup.is_reffered == '1'"
            class="d-inline-flex align-items-center badge badge-warning"
          >
            <material-icon name="gpp_good" size="16" class="mr-1" />
            Dirujuk
            <span
              v-if="can('view-mental-health-checkup-refferal-letter')"
              class="d-inline-flex align-items-center pointer"
              @click="downloadRefferalLetter()"
            >
              <material-icon name="circle" size="4" class="mx-2" />
              Unduh Rujukan
            </span>
          </span>
          <span class="d-inline-flex align-items-center badge badge-primary">
            <material-icon name="assignment_ind" size="16" class="mr-1" />
            <span class="mr-1">Psikolog:</span>
            <b>{{ checkup.psychologist.name }}</b>
          </span>
        </div>
      </div>
    </div>
    <!-- Download attachment -->
    <div
      v-if="
        !this.isEmpty(checkup.attachment) &&
        can('view-mental-health-checkup-attachment')
      "
      class="mt-2 mx-3 card d-flex align-items-center justify-content-between"
    >
      <span>Laporan telah tersedia.</span>
      <button
        class="btn btn-primary btn-sm btn-rounded d-inline-flex align-items-center"
        @click="downloadAttachment()"
      >
        <material-icon
          name="file_download"
          size="18"
          type="outlined"
          class="mr-1"
        />
        Unduh Laporan
      </button>
    </div>
    <div
      v-if="can('update-mental-health-checkup')"
      class="d-flex justify-content-center"
    >
      <button
        v-if="checkup.status == 'scheduled'"
        class="mt-3 btn btn-success btn-rounded d-inline-flex align-items-center leading-none"
        @click="openGiveSuggestionModal()"
      >
        <material-icon
          name="emoji_objects"
          type="rounded"
          size="18"
          class="mr-1"
        />
        Berikan Solusi
      </button>
      <button
        v-if="
          ['scheduled', 'completed'].includes(checkup.status) &&
          this.isEmpty(checkup.attachment)
        "
        class="mt-3 btn btn-primary btn-rounded d-inline-flex align-items-center leading-none"
        @click="openFileUploadModal()"
      >
        <material-icon
          name="file_upload"
          type="outlined"
          size="18"
          class="mr-1"
        />
        Unggah Laporan
      </button>
    </div>
    <!-- Assign schedule modal -->
    <sweet-modal
      ref="assignSchedule"
      title="Jadwal Konsultasi"
      overlay-theme="dark"
      blocking
      class="sweet-modal-with-date"
    >
      <p>
        Silakan atur tanggal untuk jadwal konsultasi. Adapun pilihan jadwal yang
        dipilih oleh pendaftar adalah pada
        <b>
          {{
            getScheduleOptionText(
              checkup.schedule.day,
              checkup.schedule.time_start,
              checkup.schedule.time_end
            )
          }}
        </b>
      </p>
      <br />
      <c-input-date
        label="Tanggal"
        class="mb-3"
        :error="!isEmpty(errors.scheduledAt)"
        :error-message="errors.scheduledAt"
      >
        <datetime
          type="date"
          format="dd/LL/yyyy"
          value-zone="local"
          v-model="scheduledAt"
          :disabled-days="disabledDaysForSchedule"
          :min-datetime="minScheduleDate"
        />
      </c-input-date>
      <div class="form-group input-self-error">
        <label>Ruang</label>
        <v-select
          placeholder="Pilih ruang..."
          label="name"
          :options="rooms"
          :reduce="(room) => room.id"
          :clearable="false"
          :class="{ 'input-error': errors.roomId }"
          v-model="roomId"
        >
          <div slot="no-options">Tidak ada ruang tersedia.</div>
        </v-select>
        <p class="self-error-message" v-if="errors.roomId">
          {{ errors.roomId }}
        </p>
      </div>
      <div class="form-group input-self-error">
        <label>Psikolog</label>
        <v-select
          placeholder="Pilih psikolog..."
          label="name"
          :options="psychologists"
          :reduce="(psychologist) => psychologist.id"
          :clearable="false"
          :class="{ 'input-error': errors.psychologistId }"
          v-model="psychologistId"
        >
          <div slot="no-options">Tidak ada psikolog tersedia.</div>
        </v-select>
        <p class="self-error-message" v-if="errors.psychologistId">
          {{ errors.psychologistId }}
        </p>
      </div>
      <button-loading
        class="btn btn-primary btn-block"
        @click="saveSchedule()"
        :loading="loading.saveSchedule"
        :loading-color="colorPrimary"
      >
        Simpan
      </button-loading>
    </sweet-modal>
    <!-- Give suggestion modal -->
    <sweet-modal
      ref="giveSuggestion"
      title="Berikan Solusi"
      overlay-theme="dark"
      blocking
    >
      <p>
        <b>Hal yang dikonsultasikan:</b>
      </p>
      <div class="card mt-1">
        {{ checkup.problem }}
      </div>
      <c-input-wrap
        label="Solusi/rekomendasi"
        :error="!isEmpty(errors.suggestion)"
        :error-message="errors.suggestion"
        class="mt-3"
      >
        <div class="tinymce-container-rounded">
          <tiny-editor v-model="suggestion" :init="tinyConfig" />
        </div>
      </c-input-wrap>
      <label class="d-flex">
        <input type="checkbox" v-model="isReffered" />
        <span class="ml-2">Dirujuk</span>
      </label>
      <div class="form-group input-self-error mt-2">
        <label>Psikolog</label>
        <v-select
          placeholder="Pilih psikolog..."
          label="name"
          :options="psychologists"
          :reduce="(psychologist) => psychologist.id"
          :clearable="false"
          :class="{ 'input-error': errors.psychologistId }"
          v-model="psychologistId"
        >
          <div slot="no-options">Tidak ada psikolog tersedia.</div>
        </v-select>
        <p class="self-error-message" v-if="errors.psychologistId">
          {{ errors.psychologistId }}
        </p>
      </div>
      <button-loading
        class="btn btn-primary btn-block"
        @click="saveSuggestion()"
        :loading="loading.saveSuggestion"
        :loading-color="colorPrimary"
      >
        Simpan
      </button-loading>
    </sweet-modal>
    <!-- File upload modal -->
    <sweet-modal ref="fileUpload" title="Laporan" overlay-theme="dark" blocking>
      <c-input
        label="Pilih Dokumen"
        sub-label="Dokumen PDF maksimal 2 MB"
        type="file"
        class="mb-1 mt-3"
        :error="!isEmpty(errors.attachment)"
        :error-message="errors.attachment"
        accept=".pdf"
        @file-change="handleFileChange"
      />
      <button-loading
        class="btn btn-primary btn-block"
        @click="saveAttachment()"
        :loading="loading.saveAttachment"
        :loading-color="colorPrimary"
      >
        Unggah
      </button-loading>
    </sweet-modal>
    <!-- Send whatsapp message modal -->
    <sweet-modal
      ref="sendWhatsappMessage"
      title="Pesan Whatsapp"
      overlay-theme="dark"
      blocking
    >
      <p class="note">
        Setelah tombol kirim diklik, Anda akan diarahkan ke aplikasi WhatsApp,
        lalu kirim pesan seperti biasanya.
      </p>
      <div class="whatsapp-message-wrap mt-2">
        <div class="d-flex justify-content-end gap-1">
          <span
            title="Indonesia"
            class="badge pointer"
            :class="{ 'badge-primary': whatsappMessageLang == 'id' }"
            @click="whatsappMessageLang = 'id'"
          >
            ID
          </span>
          <span
            title="English"
            class="badge pointer"
            :class="{ 'badge-primary': whatsappMessageLang == 'en' }"
            @click="whatsappMessageLang = 'en'"
          >
            EN
          </span>
        </div>
        <c-input
          type="textarea"
          v-model="whatsappMessage"
          placeholder="Masukkan pesan..."
          rows="12"
          class="mb-1 mt-1"
          spellcheck="false"
          :error="!isEmpty(errors.whatsappMessage)"
          :error-message="errors.whatsappMessage"
        />
      </div>
      <button class="btn btn-primary btn-block" @click="sendWhatsappMessage()">
        Kirim
      </button>
    </sweet-modal>
  </page-content>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { SweetModal } from "sweet-modal-vue";
import { camelize } from "@ridi/object-case-converter";
import { populateFormErrors } from "@/helpers/form";
import sanitize from "sanitize-filename";

import TinyEditor from "@tinymce/tinymce-vue";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver/theme";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/skins/content/default/content.min.css";
import "tinymce/plugins/link";
import "tinymce/plugins/autolink";
import "tinymce/plugins/advlist";
import "tinymce/plugins/lists";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/paste";

import {
  getScheduleOptionText,
  getCheckupStatus,
  getCheckupStatusIndo,
  getCheckupBadge,
  getWhatsappMessageTemplate,
} from "@/helpers/mental-health";

import { normalizeWhatsappNumber, getWhatsappLink } from "@/helpers/whatsapp";
import { greetingByTime } from "@/helpers/greeting";

import MentalHealthCheckupHistory from "./components/MentalHealthCheckupHistory.vue";

export default {
  name: "mental-health-participant-detail",
  components: {
    SweetModal,
    TinyEditor,
    MentalHealthCheckupHistory,
  },
  data: function () {
    return {
      id: 0,
      checkup: null,
      scheduledAt: "",
      roomId: "",
      psychologistId: "",
      suggestion: "",
      psychologist: "",
      attachment: "",
      isReffered: false,
      whatsappMessageList: {
        id: "",
        en: "",
      },
      whatsappMessageLang: "id",
      errors: {
        scheduledAt: "",
        roomId: "",
        psychologistId: "",
        suggestion: "",
        psychologist: "",
        attachment: "",
        whatsappMessage: "",
      },
      loading: {
        saveSchedule: false,
        saveSuggestion: false,
        saveAttachment: false,
      },
      tinyConfig: {
        branding: false,
        menubar: false,
        statusbar: false,
        height: 150,
        plugins: ["advlist autolink lists link", "searchreplace", "paste"],
        toolbar: "undo redo | bold italic underline | bullist numlist | link",
        content_style:
          "body { font-family: 'Roboto', sans-serif; font-size: 14px; }",
      },
      minScheduleDate: LuxonDateTime.local().toISO(),
      showAllCheckupHistories: false,
    };
  },
  computed: {
    ...mapGetters({
      rooms: "mentalHealth/room/rooms",
      psychologists: "mentalHealth/psychologist/psychologists",
    }),
    disabledDaysForSchedule: function () {
      const days = [1, 2, 3, 4, 5, 6, 7];
      days.splice(this.checkup.schedule.day, 1);
      return days;
    },
    roomOptions: function () {
      return this.rooms.map((room) => {
        return {
          value: room.id,
          text: room.name,
        };
      });
    },
    whatsappMessage: {
      get: function () {
        return this.whatsappMessageList[this.whatsappMessageLang];
      },
      set: function (value) {
        this.whatsappMessageList[this.whatsappMessageLang] = value;
      },
    },
    checkupHistories() {
      const checkupHistories = this.checkup?.user?.mental_health_checkups ?? [];
      return this.showAllCheckupHistories
        ? checkupHistories
        : [checkupHistories[0]];
    },
  },
  methods: {
    getScheduleOptionText,
    getCheckupStatus,
    getCheckupStatusIndo,
    getCheckupBadge,
    getCheckup: function () {
      this.startPageLoading();
      axios
        .get(this.apiBaseUrl + "mental-health/checkups/" + this.id, {
          params: {
            histories: true,
          },
        })
        .then((response) => {
          this.stopPageLoading();
          this.checkup = response.data.data;
          this.checkup.status = this.getCheckupStatus(this.checkup);

          if (this.checkup.psychologist) {
            this.psychologistId = this.checkup.psychologist.id;
          }

          this.initWhatsappMessage();
        })
        .catch(() => {
          this.$toasted.error("Data tidak ditemukan.");
        });
    },
    openAssignScheduleModal: function () {
      this.$refs.assignSchedule.open();
    },
    closeAssignScheduleModal: function () {
      this.$refs.assignSchedule.close();
    },
    openGiveSuggestionModal: function () {
      this.$refs.giveSuggestion.open();
    },
    closeGiveSuggestionModal: function () {
      this.$refs.giveSuggestion.close();
    },
    openFileUploadModal: function () {
      this.$refs.fileUpload.open();
    },
    closeFileUploadModal: function () {
      this.$refs.fileUpload.close();
    },
    openSendWhatsappMessageModal: function () {
      this.$refs.sendWhatsappMessage.open();
    },
    closeSendWhatsappMessageModal: function () {
      this.$refs.sendWhatsappMessage.close();
    },
    cancelSchedule: function () {
      this.$dialog
        .confirm(
          {
            title: "Konfirmasi",
            body: `Apakah yakin membatalkan konsultasi <b>${this.checkup.user.fullname}</b>?`,
          },
          { customClass: "delete-confirmation" }
        )
        .then(() => {
          axios
            .delete(this.apiBaseUrl + "mental-health/checkups/" + this.id)
            .then(() => {
              this.checkup = Object.assign({}, this.checkup, {
                status: "canceled",
                deleted_at: new Date(),
              });
              this.$toasted.success("Berhasil dibatalkan.");
            })
            .catch(() => {
              this.$toasted.error("Tidak dapat membatalkan.");
            });
        })
        .catch(() => {
          // Do nothing
        });
    },
    update: function (data) {
      return axios.post(
        this.apiBaseUrl + "mental-health/checkups/" + this.id,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    saveSchedule: function () {
      this.loading.saveSchedule = true;

      // Reset error
      this.errors.scheduledAt = "";
      this.errors.roomId = "";

      const form = new FormData();
      form.append("_method", "PUT");
      form.append("scheduled_at", this.scheduledAt);
      form.append("room_id", this.roomId);
      form.append("psychologist_id", this.psychologistId);

      this.update(form)
        .then((response) => {
          this.loading.saveSchedule = false;

          // Update current data
          this.checkup = Object.assign({}, response.data.data, {
            status: "scheduled",
          });

          this.$toasted.success("Jadwal konsultasi berhasil disimpan.");
          this.closeAssignScheduleModal();
          this.initWhatsappMessage();
        })
        .catch((error) => {
          this.loading.saveSchedule = false;

          const response = error.response;
          const code = response.status;

          if (code === 422) {
            populateFormErrors(this.errors, camelize(response.data.message));
            this.$toasted.error("Data belum benar.");
          } else {
            this.$toasted.error("Terjadi kesalahan.");
          }
        });
    },
    saveSuggestion: function () {
      this.loading.saveSuggestion = true;

      // Reset error
      this.errors.suggestion = "";
      this.errors.psychologistId = "";

      const form = new FormData();
      form.append("_method", "PUT");
      form.append("suggestion", this.suggestion);
      form.append("psychologist_id", this.psychologistId);
      form.append("is_reffered", this.isReffered);

      this.update(form)
        .then((response) => {
          this.loading.saveSuggestion = false;

          // Update current data
          this.checkup = Object.assign({}, response.data.data, {
            status: "completed",
          });

          this.$toasted.success("Solusi/rekomendasi berhasil disimpan.");
          this.closeGiveSuggestionModal();
        })
        .catch((error) => {
          this.loading.saveSuggestion = false;

          const response = error.response;
          const code = response.status;

          if (code === 422) {
            populateFormErrors(this.errors, camelize(response.data.message));
            this.$toasted.error("Data belum benar.");
          } else {
            this.$toasted.error("Terjadi kesalahan.");
          }
        });
    },
    handleFileChange: function (files) {
      this.attachment = files[0];
    },
    saveAttachment: function () {
      this.loading.saveAttachment = true;

      // Reset error
      this.errors.attachment = "";

      const form = new FormData();
      form.append("_method", "PUT");
      form.append("attachment", this.attachment);

      this.update(form)
        .then((response) => {
          this.loading.saveAttachment = false;

          const data = response.data.data;

          // Update current data
          this.checkup = Object.assign({}, this.checkup, {
            attachment: data.attachment,
          });

          this.$toasted.success("Laporan berhasil diunggah.");
          this.closeFileUploadModal();
        })
        .catch((error) => {
          this.loading.saveAttachment = false;

          const response = error.response;
          const code = response.status;

          if (code === 422) {
            populateFormErrors(this.errors, camelize(response.data.message));
            this.$toasted.error("Data belum benar.");
          } else {
            this.$toasted.error("Terjadi kesalahan.");
          }
        });
    },
    downloadAttachment: function () {
      axios
        .get(
          this.apiBaseUrl + "mental-health/checkups/" + this.id + "/attachment"
        )
        .then((response) => {
          const pdf = response.data.data;
          const linkSource = `data:application/pdf;base64,${pdf}`;
          const downloadLink = document.createElement("a");
          const filename =
            sanitize(
              "Laporan-" +
                this.checkup.user.fullname +
                "-" +
                new Date().getTime(),
              { replacement: "-" }
            ) + ".pdf";
          downloadLink.href = linkSource;
          downloadLink.download = filename;
          downloadLink.click();

          this.$toasted.success("Dokumen berhasil diunduh.");
        })
        .catch((error) => {
          const response = error.response;
          const code = response.status;

          if (code === 404) {
            this.$toasted.error("Dokumen laporan belum tersedia.");
          } else {
            this.$toasted.error("Terjadi kesalahan.");
          }
        });
    },
    downloadRefferalLetter: function () {
      axios
        .get(
          this.apiBaseUrl +
            "mental-health/checkups/" +
            this.id +
            "/refferal-letter"
        )
        .then((response) => {
          const docx = response.data.data;
          const linkSource = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${docx}`;
          const downloadLink = document.createElement("a");
          const filename =
            sanitize(
              "Surat-Rujukan-" +
                this.checkup.user.fullname +
                "-" +
                new Date().getTime(),
              { replacement: "-" }
            ) + ".docx";
          downloadLink.href = linkSource;
          downloadLink.download = filename;
          downloadLink.click();

          this.$toasted.success("Dokumen berhasil diunduh.");
        })
        .catch(() => {
          this.$toasted.error("Terjadi kesalahan.");
        });
    },
    getWhatsappMessage: function (lang) {
      const greeting = greetingByTime(lang);
      const messageTemplate = getWhatsappMessageTemplate(lang);
      const date = this.checkup.scheduled_at
        ? this.formatDateForHuman(this.checkup.scheduled_at, true, lang)
        : "...";
      const time =
        this.formatTimeToIndo(this.checkup.schedule.time_start) +
        " - " +
        this.formatTimeToIndo(this.checkup.schedule.time_end) +
        " WIB";
      const room = this.checkup?.room?.name ?? "...";

      return messageTemplate
        .replace("{greeting}", greeting)
        .replace("{name}", this.checkup.user.fullname)
        .replace("{date}", date)
        .replace("{time}", time)
        .replace("{room}", room);
    },
    initWhatsappMessage: function () {
      this.whatsappMessageList.id = this.getWhatsappMessage("id");
      this.whatsappMessageList.en = this.getWhatsappMessage("en");
    },
    sendWhatsappMessage: function () {
      const phone = normalizeWhatsappNumber(this.checkup.user.phone);
      const url = getWhatsappLink(phone, this.whatsappMessage);
      window.open(url, "_blank");
    },
  },
  created: function () {
    this.id = this.$route.params.id;
    this.getCheckup();
    this.$store.dispatch("mentalHealth/room/getAll");
    this.$store.dispatch("mentalHealth/psychologist/getAll", {
      activeOnly: true,
    });
  },
};
</script>
